'use client';

import React, { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

interface LoginDialogProps {
    open: boolean;
    idPath?: string;
    mapTitle?: string;
    onClose: () => void;
    onSuccess: (authData: { accessToken: string; id_path: string; user: unknown }) => void;
}

/**
 * LoginDialog for minimap map-level authentication.
 *
 * IMPORTANT — iOS Safari fix:
 * We intentionally bypass NextAuth's `signIn` helper for this flow.
 * `signIn('credentials', { redirect: false })` triggers a POST to
 * `/api/auth/callback/credentials` which sets a session cookie.
 * On iOS Safari, Intelligent Tracking Prevention (ITP) may block or
 * delay the Set-Cookie, causing NextAuth to think the session is
 * invalid.  The resulting retry loop manifests as:
 *   "A problem repeatedly occurred on this web page"
 *
 * Instead, we call the project login API directly via `fetch` and
 * return the access token to the parent component, which stores it
 * in React state.  This avoids all cookie/redirect issues on Safari.
 */
export default function LoginDialog({ open, idPath, mapTitle, onClose, onSuccess }: LoginDialogProps) {
    const t = useTranslations();
    const [usernameOrEmail, setUsernameOrEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const submitLockRef = useRef(false);

    if (!open) {
        return null;
    }

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isLoading) {
            void handleSubmit();
        }
    };

    const handleSubmit = async () => {
        if (submitLockRef.current || isLoading) {
            return;
        }

        submitLockRef.current = true;
        setErrorMessage('');

        if (!idPath) {
            setErrorMessage(t('LOGIN_DIALOG_ERR_NO_PROJECT'));
            submitLockRef.current = false;
            return;
        }

        if (!usernameOrEmail || !password) {
            setErrorMessage(t('LOGIN_DIALOG_ERR_MISSING_CREDENTIALS'));
            submitLockRef.current = false;
            return;
        }

        try {
            setIsLoading(true);

            // iPhone Safari is sensitive to keyboard + async UI transitions.
            // Explicitly blurring active input reduces viewport jump loops.
            if (typeof document !== 'undefined') {
                const active = document.activeElement as HTMLElement | null;
                active?.blur?.();
            }

            const apiBase = '/api/proxy';
            const loginUrl = `${apiBase}/v1/projects/users/login?id_path=${encodeURIComponent(idPath)}`;

            const response = await fetch(loginUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ usernameOrEmail, password }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                // Handle specific error cases
                if (response.status === 403) {
                    if (data.requireVerification) {
                        setErrorMessage(t('LOGIN_DIALOG_ERR_NOT_VERIFIED') || 'Tài khoản chưa được xác thực email.');
                    } else if (data.requireApproval || data.code === 'ACCOUNT_PENDING_APPROVAL' || data.approvalStatus === 'pending') {
                        setErrorMessage(t('LOGIN_DIALOG_ERR_PENDING_APPROVAL') || 'Tài khoản đang chờ phê duyệt.');
                    } else if (data.accountRejected || data.code === 'ACCOUNT_REJECTED' || data.approvalStatus === 'rejected') {
                        setErrorMessage(t('LOGIN_DIALOG_ERR_REJECTED') || 'Tài khoản đã bị từ chối.');
                    } else {
                        setErrorMessage(data.message || t('LOGIN_DIALOG_ERR_LOGIN_FAILED'));
                    }
                } else if (response.status === 401) {
                    setErrorMessage(t('LOGIN_DIALOG_ERR_LOGIN_FAILED'));
                } else {
                    setErrorMessage(data.error || data.message || t('LOGIN_DIALOG_ERR_LOGIN_FAILED'));
                }
                return;
            }

            // Extract auth data from response
            const user = data.data?.user || data.user || data.data || data;
            const accessToken = data.data?.accessToken || data.accessToken;
            const id_path = data.data?.id_path || data.id_path || idPath;

            if (!accessToken) {
                setErrorMessage(t('LOGIN_DIALOG_ERR_SESSION_INCOMPLETE'));
                return;
            }

            setUsernameOrEmail('');
            setPassword('');
            onSuccess({ accessToken, id_path, user });
        } catch {
            setErrorMessage(t('LOGIN_DIALOG_ERR_GENERIC'));
        } finally {
            setIsLoading(false);
            submitLockRef.current = false;
        }
    };

    return (
        <div className="fixed inset-0 z-[1200] bg-black/40 flex items-start justify-center overflow-y-auto p-4 pt-[10vh] pb-[50vh]">
            <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl flex-shrink-0">
                <h3 className="text-lg font-bold text-black">{t('LOGIN_DIALOG_TITLE')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                    {mapTitle
                        ? t('LOGIN_DIALOG_DESCRIPTION_WITH_MAP', { mapTitle })
                        : t('LOGIN_DIALOG_DESCRIPTION')}
                </p>

                <form
                    className="mt-4 space-y-3"
                    onSubmit={handleFormSubmit}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            event.stopPropagation();
                            if (!isLoading) {
                                void handleSubmit();
                            }
                        }
                    }}
                >
                    <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">{t('LOGIN_DIALOG_ACCOUNT_LABEL')}</label>
                        <input
                            type="text"
                            value={usernameOrEmail}
                            onChange={(e) => setUsernameOrEmail(e.target.value)}
                            onFocus={(e) => {
                                // Scroll input into view when keyboard opens on iOS
                                setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                            }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder={t('LOGIN_DIALOG_ACCOUNT_PLACEHOLDER')}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">{t('LOGIN_DIALOG_PASSWORD_LABEL')}</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={(e) => {
                                // Scroll input into view when keyboard opens on iOS
                                setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                            }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            placeholder={t('LOGIN_DIALOG_PASSWORD_PLACEHOLDER')}
                        />
                    </div>

                    {errorMessage && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                            {errorMessage}
                        </div>
                    )}

                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onClose();
                            }}
                            className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-medium text-gray-700"
                            disabled={isLoading}
                        >
                            {t('LOGIN_DIALOG_CANCEL')}
                        </button>
                        <button
                            type="submit"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (!isLoading) {
                                    void handleSubmit();
                                }
                            }}
                            className="flex-1 bg-black text-white rounded-lg py-2 text-sm font-medium disabled:opacity-60"
                            disabled={isLoading}
                        >
                            {isLoading ? t('LOGIN_DIALOG_LOADING') : t('LOGIN_DIALOG_SUBMIT')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
